import React, {
	type FC,
	forwardRef,
	type Ref,
	type RefObject,
	useCallback,
	useLayoutEffect,
} from 'react';

import { GlobalPortal } from '@shared/ui/global-portal';

import { DropDownProps } from './types';

import styles from './styles.module.scss';

/**
 * DropDownComponent – функциональная версия без MobX.
 * Названия пропсов не изменены.
 */
const DropDownComponent: FC<DropDownProps> = ({
	refParent,
	refDropdown,
	floatRight,
	disableMinWidth,
	defaultDirection,
	minWidth,
	offsetTop = 0,
	offsetLeft = 0,
	children,
}) => {
	const isMiddle = useCallback(() => defaultDirection?.includes('middle'), [defaultDirection]);

	const isRight = useCallback(() => defaultDirection?.includes('right'), [defaultDirection]);

	const setPosition = useCallback(() => {
		const parentEl = refParent.current;
		const dropEl = (refDropdown as RefObject<HTMLDivElement>).current;
		if (!parentEl || !dropEl) return;

		const offset = 4;
		const windowHeight = document.documentElement.clientHeight;
		const windowWidth = document.documentElement.clientWidth;

		let scrollTop = window.pageYOffset;
		const { position: bodyPos, top: bodyTop } = document.body.style;

		// учёт зафиксированного body (модалки на мобилках)
		if (!scrollTop && bodyPos === 'fixed' && bodyTop) {
			scrollTop = -Number.parseInt(bodyTop, 10);
		}

		const scrollLeft = window.pageXOffset;
		const parentRect = parentEl.getBoundingClientRect();

		if (!disableMinWidth) {
			dropEl.style.minWidth = `${minWidth ?? parentRect.width}px`;
		}

		const dropRect = dropEl.getBoundingClientRect();

		/* ---------- top ---------- */
		let top;
		if (isMiddle() && parentRect.top + dropRect.height + offset < windowHeight) {
			top = parentRect.top + scrollTop + offsetTop;
		} else if (parentRect.top + parentRect.height + dropRect.height + offset < windowHeight) {
			top = parentRect.top + scrollTop + parentRect.height + offset + offsetTop;
		} else if (parentRect.top > dropRect.height + offset) {
			top = parentRect.top + scrollTop - (dropRect.height + offset) + offsetTop;
		} else {
			const initTop = parentRect.top + parentRect.height + offset;
			top = initTop + scrollTop - (initTop + dropRect.height - windowHeight) - offset + offsetTop;
		}

		/* ---------- left ---------- */
		let left;
		if (floatRight) {
			left = parentRect.left - (dropRect.width - parentRect.width) + scrollLeft + offsetLeft;

			if (left < 0) left = parentRect.left + scrollLeft;

			if (left + dropRect.width + offset > windowWidth) {
				left =
					parentRect.left -
					(parentRect.left + dropRect.width + offset - windowWidth) +
					scrollLeft +
					offsetLeft;
			}
		} else {
			left = parentRect.left + scrollLeft + offsetLeft;

			if (isRight() && parentRect.left + dropRect.width * 2 + offset < windowWidth) {
				left = parentRect.left + dropRect.width - offset * 2 + scrollLeft + offsetLeft;
			}

			if (parentRect.left + dropRect.width + offset > windowWidth) {
				left =
					parentRect.left -
					(parentRect.left + dropRect.width + offset - windowWidth) +
					scrollLeft +
					offsetLeft;
			}
		}

		/* ---------- apply ---------- */
		dropEl.style.left = `${left}px`;
		dropEl.style.top = `${top}px`;
		dropEl.style.position = 'absolute';
	}, [
		refParent,
		refDropdown,
		floatRight,
		disableMinWidth,
		minWidth,
		defaultDirection,
		offsetTop,
		offsetLeft,
		isMiddle,
		isRight,
	]);

	// аналог componentDidMount + componentDidUpdate
	useLayoutEffect(() => {
		setPosition();
	});

	return (
		<GlobalPortal>
			<div ref={refDropdown} className={styles.Dropdown}>
				{children}
			</div>
		</GlobalPortal>
	);
};

export const DropDown = forwardRef<HTMLDivElement, DropDownProps>(
	(props, ref: Ref<HTMLDivElement> | null) => <DropDownComponent {...props} refDropdown={ref} />
);
